import { z } from "zod";
import { noNulChar } from "./homework-material.ts";

/**
 * Ответ ученика текстом к домашке с материалом (1.8
 * `docs/homework-scratch-design.md`, просьба владельца 26.09.2026).
 *
 * Один ответ на домашку, а не на картинку: у картинок материала нет номеров
 * заданий, и делить ответ по ним значило бы угадывать. Отправка заменяет
 * прошлый ответ целиком (`PUT /v1/homework/:homeworkId/answer`).
 *
 * ⚠️ **Наружу ответ не уезжает никогда.** Свободный ответ ученика несёт ПДн, и
 * исключение раздела 5.9 CLAUDE.md (перевод заданного текста) на него не
 * распространяется. Ни модели, ни прокси.
 *
 * Отдельным файлом от `./homework-material.ts`: там то, что задаёт
 * преподаватель, здесь то, что пишет ученик, и срок хранения у них разный.
 * Этот модуль не импортирует `./homework.ts` (цикл дал бы `undefined` на месте
 * схемы).
 */

/**
 * Потолок ответа в знаках. Тот же, что у текста задания: ответ на условие в
 * четыре страницы не обязан быть короче условия. В базе тем же числом стоит
 * CHECK `homework_answer_text_len`.
 */
export const HOMEWORK_ANSWER_MAX = 10_000;

/**
 * Сколько суток хранится СОДЕРЖИМОЕ ответа: у попыток и у ответа текстом к
 * домашке (2.3 `docs/homework-design.md`, решение владельца 16.08.2026). Это
 * умолчание `ATTEMPT_ANSWER_RETENTION_DAYS` сервера и то же число, которое
 * называет экран на месте стёртого ответа: одно место, а не два.
 */
export const ATTEMPT_ANSWER_RETENTION_DAYS_DEFAULT = 30;

/**
 * Сколько отправок ответа в минуту принимаем от одной сессии. Отправка это
 * запись в строку домашки, и ученик может править и отправлять снова; тридцать
 * в минуту это вдвое больше, чем человек успеет нажать, а залипшую вкладку
 * останавливает.
 */
export const HOMEWORK_ANSWER_RATE_MAX = 30;

/**
 * Текст ответа как он приезжает. Переносы и отступы сохраняются как есть (у
 * преподавателя `white-space: pre-wrap`), поэтому схема НЕ режет пробелы, но
 * пустой по существу ответ (одни пробелы) не принимает: «отправил пустоту» не
 * сдача.
 */
export const HomeworkAnswerText = z
  .string()
  .max(HOMEWORK_ANSWER_MAX)
  .refine((text) => text.trim() !== "", { message: "пустой ответ" })
  // Postgres не хранит U+0000 в `text`: без отказа здесь был бы 500
  .refine(noNulChar, { message: "символ U+0000 в тексте" });

/** Тело `PUT /v1/homework/:homeworkId/answer`. */
export const HomeworkAnswerRequest = z.object({ text: HomeworkAnswerText });
export type HomeworkAnswerRequest = z.infer<typeof HomeworkAnswerRequest>;

/**
 * Ответ отправки. `studentDoneAt` едет сразу, потому что первая отправка пишет
 * факт «ученик сделал» (1.8), и шапка ленты пересчитывает статус по нему, не
 * дожидаясь перечитывания карточки. Повтор момент сдачи не двигает.
 */
export const HomeworkAnswerResponse = z.object({
  answerAt: z.number().int(),
  studentDoneAt: z.number().int(),
});
export type HomeworkAnswerResponse = z.infer<typeof HomeworkAnswerResponse>;

/**
 * Тело отметки просмотра: момент ответа, который преподаватель УВИДЕЛ на
 * экране. Отметка ставится, только если на сервере ответ не новее увиденного:
 * иначе ответ, переотправленный, пока открыты результаты, погас бы непрочитанным.
 */
export const HomeworkAnswerViewedRequest = z.object({ answerAt: z.number().int() });
export type HomeworkAnswerViewedRequest = z.infer<typeof HomeworkAnswerViewedRequest>;

/**
 * Ответ отметки просмотра (`POST /v1/homework/:homeworkId/answer/viewed`).
 * `null` означает «ответа нет, отмечать нечего».
 */
export const HomeworkAnswerViewedResponse = z.object({
  answerViewedAt: z.number().int().nullable(),
});
export type HomeworkAnswerViewedResponse = z.infer<typeof HomeworkAnswerViewedResponse>;

/**
 * Ответ был, а текст стёрт по сроку хранения. Отправка пустой текст не
 * принимает, поэтому «момент есть, текста нет» получается ровно одним путём:
 * уборщиком. Признак выводится, а не хранится, чтобы у правды было одно место.
 */
export function homeworkAnswerErased(args: {
  answerAt: number | null;
  answerText: string | null;
}): boolean {
  return args.answerAt !== null && args.answerText === null;
}

/**
 * Есть ли у домашки материал, на который отвечают текстом: текст задания или
 * хотя бы один файл материала, картинка, аудио или видео (1.8;
 * `docs/homework-media-plan.md`, Р1). Сервер проверяет то же самое под
 * блокировкой строки (`exists ... homework_material` без вида файла); здесь это
 * о том, рисовать ли поле. Без `media` домашка из одного аудио (изложение) не
 * получила бы поля, хотя сервер ответ принимает.
 *
 * `media` необязательно: карточка от сервера до 16.10.2026 его не несла.
 */
export function homeworkHasMaterial(args: {
  taskText: string | null;
  material: readonly unknown[];
  media?: readonly unknown[] | undefined;
}): boolean {
  return (
    (args.taskText !== null && args.taskText.trim() !== "") ||
    args.material.length > 0 ||
    (args.media?.length ?? 0) > 0
  );
}
